import { EXTRA_CREDIT_PACK, additionalAddOns, plans } from "./pricingData";

// Illustrative assumptions: each month Dibs re-engages a share of stored contacts, and some of them return.
export const REENGAGE_SHARE = 0.1;
export const WIN_BACK_RATE = 0.1;

export type CalculatorInputs = {
  billing: "monthly" | "yearly";
  /** Upper bound of the chosen range, used to pick a plan that fits. */
  contacts: number;
  /** Typical contacts in the range, used for the revenue estimate (defaults to `contacts`). */
  contactsTypical?: number;
  branches: number;
  staff: number;
  advanced: boolean;
  bookings: boolean;
  businessNumbers: boolean;
  spendPerCustomer: number;
  monthlyRevenue: number;
};

export type CalculatorResult = {
  plan: string | null;
  planPrice: number;
  extraPacks: number;
  extraCreditsCost: number;
  addOnsCost: number;
  totalCost: number | null;
  creditsNeeded: number;
  recovered: number;
  net: number | null;
  returnMultiple: number | null;
  revenueIncrease: number;
};

const addOnPrice = (name: string) => additionalAddOns.find((a) => a.name === name)?.price ?? 0;

export function calculate(input: CalculatorInputs): CalculatorResult {
  const reengaged = Math.round((input.contactsTypical ?? input.contacts) * REENGAGE_SHARE);
  const creditsNeeded = reengaged * (input.advanced ? 3 : 1);
  const addOnsCost = (input.bookings ? addOnPrice("Dibs Bookings") : 0) + (input.businessNumbers ? addOnPrice("Business numbers") : 0);
  const recovered = reengaged * WIN_BACK_RATE * input.spendPerCustomer;
  const revenueIncrease = input.monthlyRevenue > 0 ? recovered / input.monthlyRevenue : 0;

  const options = plans.flatMap((plan) => {
    const { capacity, prices, extraCreditCap = 0 } = plan;
    if (!capacity || !prices) return [];
    const fits =
      input.contacts <= capacity.storage &&
      input.branches <= capacity.branches &&
      input.staff <= capacity.staff &&
      (!input.advanced || capacity.advanced) &&
      creditsNeeded <= capacity.credits + extraCreditCap;
    if (!fits) return [];
    const extraPacks = Math.ceil(Math.max(0, creditsNeeded - capacity.credits) / EXTRA_CREDIT_PACK.credits);
    const extraCreditsCost = extraPacks * EXTRA_CREDIT_PACK.price;
    const planPrice = prices[input.billing];
    return [{ plan: plan.name, planPrice, extraPacks, extraCreditsCost, totalCost: planPrice + extraCreditsCost + addOnsCost }];
  });

  const best = options.sort((a, b) => a.totalCost - b.totalCost)[0];
  if (!best) {
    return { plan: null, planPrice: 0, extraPacks: 0, extraCreditsCost: 0, addOnsCost, totalCost: null, creditsNeeded, recovered, net: null, returnMultiple: null, revenueIncrease };
  }
  return { ...best, addOnsCost, creditsNeeded, recovered, net: recovered - best.totalCost, returnMultiple: best.totalCost > 0 ? recovered / best.totalCost : null, revenueIncrease };
}
